import {
  AccountsApi,
  CarColorsApi,
  CarTypesApi,
  CompaniesApi,
  DriversApi,
  GroupsApi,
  Configuration as OrderConfiguration,
  OrdersApi,
  OrganizationsApi,
  SalesOfficesApi,
  VehiclesApi,
} from "@generated/order";
import {
  BillingsApi,
  OperationsApi,
  OperatorsApi,
  CompaniesApi as PaymentCompaniesApi,
  ConditionsApi as PaymentConditionsApi,
  Configuration as PaymentConfiguration,
  GroupsApi as PaymentGroupsApi,
} from "@generated/payment";
import {
  type FetchAPI,
  type HTTPHeaders,
  OpenSearchApi,
  ProbeApi,
  Configuration as ProbeConfiguration,
} from "@generated/probe";
import { AppConfig } from "@shared/constants/AppConfig";
import { PagePaths } from "@shared/constants/PagePaths";
import { RedirectHandler } from "@shared/utils/redirectHandler";
import { AuthApi } from "./AuthApi";

export class ApiFactory {
  private constructor() {}

  public static readonly fetchWithRedirect: FetchAPI = async (url, init) => {
    try {
      const response = await fetch(url, init);

      // Orders APIの仕様として、リソースが存在しない場合は404を返すため
      if (response.status === 404) {
        return response;
      }

      RedirectHandler.handleRedirect(response);

      return response;
    } catch {
      throw RedirectHandler.redirectToLogin();
    }
  };

  public static readonly credentialsInclude: RequestCredentials = "include";

  public static readonly commonHeader: Record<string, string> = {
    "Cache-Control": "no-cache",
  };

  public static readonly csvHeader: Record<string, unknown> = {
    headers: new Headers(),
  };

  public static readonly orderApiConfig = new OrderConfiguration({
    basePath: AppConfig.ApiRoots.Order,
    headers: ApiFactory.commonHeader as HTTPHeaders,
    credentials: ApiFactory.credentialsInclude,
    fetchApi: ApiFactory.fetchWithRedirect,
  });

  public static readonly probeApiConfig = new ProbeConfiguration({
    basePath: AppConfig.ApiRoots.Probe,
    headers: ApiFactory.commonHeader as HTTPHeaders,
    credentials: ApiFactory.credentialsInclude,
    fetchApi: ApiFactory.fetchWithRedirect,
  });

  public static readonly openSearchApiConfig = new ProbeConfiguration({
    basePath: AppConfig.ApiRoots.OpenSearch,
    headers: ApiFactory.commonHeader as HTTPHeaders,
    credentials: ApiFactory.credentialsInclude,
    fetchApi: ApiFactory.fetchWithRedirect,
  });

  public static readonly paymentApiConfig = new PaymentConfiguration({
    basePath: AppConfig.ApiRoots.Payment,
    headers: ApiFactory.commonHeader as HTTPHeaders,
    credentials: ApiFactory.credentialsInclude,
    fetchApi: ApiFactory.fetchWithRedirect,
  });

  public static readonly accountsApi = new AccountsApi(
    ApiFactory.orderApiConfig,
  );

  public static readonly ordersApi = new OrdersApi(ApiFactory.orderApiConfig);

  public static readonly organizationsApi = new OrganizationsApi(
    ApiFactory.orderApiConfig,
  );

  public static readonly driversApi = new DriversApi(ApiFactory.orderApiConfig);

  public static readonly vehiclesApi = new VehiclesApi(
    ApiFactory.orderApiConfig,
  );

  public static readonly groupsApi = new GroupsApi(ApiFactory.orderApiConfig);

  public static readonly companiesApi = new CompaniesApi(
    ApiFactory.orderApiConfig,
  );

  public static readonly salesOfficesApi = new SalesOfficesApi(
    ApiFactory.orderApiConfig,
  );

  public static readonly carTypesApi = new CarTypesApi(
    ApiFactory.orderApiConfig,
  );

  public static readonly carColorsApi = new CarColorsApi(
    ApiFactory.orderApiConfig,
  );

  public static readonly probeApi = new ProbeApi(ApiFactory.probeApiConfig);

  public static readonly openSearchApi = new OpenSearchApi(
    ApiFactory.openSearchApiConfig,
  );

  public static readonly operationsApi = new OperationsApi(
    ApiFactory.paymentApiConfig,
  );

  public static readonly billingsApi = new BillingsApi(
    ApiFactory.paymentApiConfig,
  );

  public static readonly operatorsApi = new OperatorsApi(
    ApiFactory.paymentApiConfig,
  );

  public static readonly paymentGroupsApi = new PaymentGroupsApi(
    ApiFactory.paymentApiConfig,
  );

  public static readonly paymentCompaniesApi = new PaymentCompaniesApi(
    ApiFactory.paymentApiConfig,
  );

  public static readonly paymentConditionsApi = new PaymentConditionsApi(
    ApiFactory.paymentApiConfig,
  );

  public static readonly authApi = new AuthApi();

  /**
   * リダイレクトを処理する
   * @param res
   * @returns boolean redirectされたかどうか
   * @description 302や401のリダイレクトを検知して、
   *              LocationヘッダーのURLにリダイレクトする。
   *              Locationヘッダーが取得できない場合はtrueを返す。
   */
  public static handleRedirect(
    res: Response | { status: number; headers: Headers },
  ): { needsRedirect: boolean; noLocation: boolean } {
    if (res.status === 302 || res.status === 401) {
      const redirectUrl = res.headers.get("Location");
      if (!redirectUrl || !/^http(s)?:\/\//.test(redirectUrl)) {
        return { needsRedirect: true, noLocation: true };
      }

      const fullUrl = redirectUrl.match(/^http(s)/)
        ? redirectUrl
        : PagePaths.abs(redirectUrl);

      window.location.replace(fullUrl);
      return { needsRedirect: true, noLocation: false };
    }
    return { needsRedirect: false, noLocation: false };
  }
}
